import * as React from 'react';
import { Rule, RulesMessages, ValidationResponse } from '../utils/validator';
import TextInput from './TextInput';
import { HatType } from './Hat';

interface DateInputProps {
  children?: any;
  name: string;
  value?: string;
  id: string;
  label: string;
  placeHolder?: string;
  required?: boolean;
  hasError?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
  unAvailableDates?: Date[];
  disablePastDates?: boolean;
  disableFutureDates?: boolean;
  errors?: any;
  theme?: 'night' | 'day';
  validationRules?: Rule[];
  rulesMessages?: RulesMessages;
  onChange?: Function;
  onComplete?: Function;
  onError?: Function;
  onBlur?: Function;
  onEsc?: Function;
  onEnter?: Function;
  onLeft?: Function;
  onRight?: Function;
  onUp?: Function;
  onDown?: Function;
  onSpace?: Function;
  hat?: HatType[];
  format?: 'MM/dd/yyyy' | 'MM/dd/yy' | 'MM/yy' | 'MM/yyyy' | 'MM/dd';
  readOnly?: boolean;
  disabled?: boolean;
  toolTip?: React.ReactNode;
  autocomplete?: string;
}

interface DateInputState {
  inputValue?: string;
  validationResponse?: ValidationResponse;
}

const isModifierKey = (event: any) => {
  const key = event.keyCode;

  return (
    event.shiftKey === true ||
    key === 35 ||
    key === 36 || // Allow Shift, Home, End
    key === 8 ||
    key === 9 ||
    key === 13 ||
    key === 46 || // Allow Backspace, Tab, Enter, Delete
    (key > 36 && key < 41) || // Allow left, up, right, down
    // Allow Ctrl/Command + A,C,V,X,Z
    ((event.ctrlKey === true || event.metaKey === true) &&
      (key === 65 || key === 67 || key === 86 || key === 88 || key === 90))
  );
};

export default function DateInput(props: DateInputProps) {
  const { format = 'MM/dd/yyyy', placeHolder = format } = props;
  const [state, setState] = React.useState<DateInputState>({
    inputValue: props.value || ''
  });
  React.useEffect(() => {
    setState({
      ...state,
      inputValue:
        props.value && props.value.replace(/\//g, ' / ').replace(/ +/g, ' ')
    });
  }, [props.value]);

  const componentPropsRules: Rule[] = [];

  componentPropsRules.push({
    ruleName: 'date',
    format: props.format,
    message: props.rulesMessages && props.rulesMessages.date,
    defaultMessage: `${props.label} is not a valid date.`
  });

  let minDateValue;
  let maxDateValue;

  if (props.minDate) {
    minDateValue =
      props.minDate instanceof Date ? props.minDate : new Date(props.minDate);
  }

  if (props.maxDate) {
    maxDateValue =
      props.maxDate instanceof Date ? props.maxDate : new Date(props.maxDate);
  }

  const minDate = props.disablePastDates
    ? new Date(new Date().toDateString())
    : minDateValue;

  const maxDate = props.disableFutureDates
    ? new Date(new Date().toDateString())
    : maxDateValue;

  if (minDate) {
    componentPropsRules.push({
      ruleName: 'after',
      format: props.format,
      ruleValue: minDate,
      message: props.rulesMessages && props.rulesMessages.after,
      defaultMessage: props.disablePastDates
        ? `${props.label} cannot be a past date.`
        : `${props.label} cannot be before ${minDate.toDateString()}.`
    });
  }

  if (maxDate) {
    componentPropsRules.push({
      ruleName: 'before',
      format: props.format,
      ruleValue: maxDate,
      message: props.rulesMessages && props.rulesMessages.before,
      defaultMessage: props.disableFutureDates
        ? `${props.label} cannot be a future date.`
        : `${props.label} cannot be after ${maxDate.toDateString()}.`
    });
  }
  if (props.unAvailableDates) {
    componentPropsRules.push({
      ruleName: 'unAvailableDates',
      format: props.format,
      ruleValue: props.unAvailableDates,
      message: props.rulesMessages && props.rulesMessages.unAvailableDates,
      defaultMessage: `${props.label} should not be from ${dateArrayToString(
        props.unAvailableDates
      )}.`
    });
  }
  function dateArrayToString(datesArray: Date[]) {
    let newStr = '';
    datesArray.forEach((item) => {
      newStr = newStr ? `${newStr}, ` : '';
      newStr = `${newStr}${item.toDateString()}`;
    });
    return newStr;
  }
  return (
    <TextInput
      placeHolder={placeHolder}
      name={props.name}
      value={state.inputValue}
      inputMode="numeric"
      id={props.id}
      required={props.required}
      label={props.label}
      hasError={props.hasError}
      errors={props.errors}
      theme={props.theme}
      aria-placeholder={placeHolder}
      rulesMessages={props.rulesMessages}
      autocomplete={props.autocomplete}
      onKeyDown={(e: any) => {
        if (
          !(
            isModifierKey(e) ||
            (e.keyCode >= 48 && e.keyCode <= 57) ||
            (e.keyCode >= 96 && e.keyCode <= 105)
          )
        ) {
          e.preventDefault();
        }
      }}
      onError={props.onError}
      onBlur={props.onBlur}
      onComplete={(date: string) => {
        if (props.onComplete) {
          props.onComplete(date.replace(/ /g, ''));
        }
      }}
      onChange={(event: any) => {
        const { target } = event;

        if (
          !state.inputValue ||
          target.value.length > state.inputValue.length
        ) {
          const _format = format.toString().replace(/\//g, ' / ');
          const firstLen = _format.split(' / ')[0].length;
          const secondLen = _format.split(' / ')[1].length + firstLen + 1;
          if (
            target.value.slice(firstLen, firstLen + 3) !== ' / ' &&
            target.value.length >= firstLen
          ) {
            target.value = `${target.value
              .replace(' ', '')
              .replace('/', '')
              .slice(0, firstLen)} / ${target.value
              .replace(' ', '')
              .replace('/', '')
              .slice(firstLen)}`;
          }
          if (
            target.value.slice(secondLen + 2, secondLen + 5) !== ' / ' &&
            format.length > secondLen &&
            target.value.slice(firstLen, firstLen + 3) === ' / ' &&
            target.value.length >= secondLen + 2
          ) {
            target.value = `${target.value.slice(
              0,
              secondLen + 2
            )} / ${target.value
              .slice(secondLen + 2)
              .replace(' ', '')
              .replace('/', '')}`;
          }
        }

        setState({
          ...state,
          inputValue: target.value
        });
        if (props.onChange !== undefined) {
          props.onChange({
            ...event,
            target: { ...event.target, value: target.value.replace(/ /g, '') }
          });
        }
      }}
      onEsc={(inputValue: string) => {
        if (props.onEsc !== undefined) {
          props.onEsc(inputValue);
        }
      }}
      onEnter={(inputValue: string) => {
        if (props.onEnter !== undefined) {
          props.onEnter(inputValue);
        }
      }}
      onUp={(inputValue: string) => {
        if (props.onUp !== undefined) {
          props.onUp(inputValue);
        }
      }}
      onDown={(inputValue: string) => {
        if (props.onDown !== undefined) {
          props.onDown(inputValue);
        }
      }}
      onLeft={(inputValue: string) => {
        if (props.onLeft !== undefined) {
          props.onLeft(inputValue);
        }
      }}
      onRight={(inputValue: string) => {
        if (props.onRight !== undefined) {
          props.onRight(inputValue);
        }
      }}
      onSpace={(inputValue: string) => {
        if (props.onSpace !== undefined) {
          props.onSpace(inputValue);
        }
      }}
      maxLength={format.length + (format.split('/').length - 1) * 2}
      validationRules={componentPropsRules}
      hat={props.hat}
      readOnly={props.readOnly}
      disabled={props.disabled}
      toolTip={props.toolTip}
    >
      {props.children}
    </TextInput>
  );
}
